import { Readex_Pro } from 'next/font/google';
import { useRouter } from 'next/router';
import { getStringValue } from '@/clientOnlyUtils/clientUtils';
import { cn } from '@poseidon/components/cn';
import FingerprintJS from '@fingerprintjs/fingerprintjs-pro';
import React from 'react';

import { Alerts } from '@poseidon/components/StrokeIcons';
import { WaveSpinner } from '@poseidon/components/WaveSpinner';

import { api } from '@/utils/api';
import { DocumentAside } from '~/components/DocumentAside';
import { DocumentContent } from '~/components/DocumentContent';
import { SEO } from '~/components/Seo';
import { env } from '~/env';

const readexPro = Readex_Pro({ subsets: ['latin'] });

type DocumentSlide = {
  id: string;
  order: number;
  url: string;
  webp: string | null;
  webpMobile: string | null;
  width: number;
  height: number;
  sizes: { w: number; h: number; url: string }[] | null;
};

type DocumentPublic = {
  title: string;
  description: string | null;
  primaryColor: string;
  secondaryColor: string;
  ogImageUrl: string | null;
  fileUrl: string;
  fileContentType: string;
  canBeIFramed: boolean | null;
  documentRepresentation: DocumentSlide[];
  url: string;
};

type PublicUserTeam = {
  gtmId: string | null;
  teamLogo: string | null;
  docUrl: string;
  teamName: string;
  email: string | null;
  name: string | null;
  title: string | null;
  profilePicture: string | null;
  contactNumber: string | null;
  calendarLink: string | null;
  calendarText: string | null;
  hubspot_owner_id: string | null;
};

interface PublicDocument {
  host?: string;
  initialSharingData?: { sharing: DocumentPublic; userTeam: PublicUserTeam };
  navigateOnNotIFramed?: boolean;
}

export const PublicDocument = ({ initialSharingData, navigateOnNotIFramed, host }: PublicDocument) => {
  const [result, setResult] = React.useState<{
    requestId: string;
    visitorId: string | undefined;
  }>();
  const router = useRouter();
  const docIdRaw = getStringValue(router.query.docId);
  const docId = docIdRaw != null ? docIdRaw.split('+')[0] : undefined;

  const { data, isLoading } = api.sharingPublic.idOrNull.useQuery(docId || '', {
    enabled: Boolean(docId),
    initialData: initialSharingData,
    retry: false, // disable retry on error
  });

  // want to not run during SSR
  // its dumb and wasteful to fingerprint out server
  const isSSR = typeof window === 'undefined';

  React.useEffect(() => {
    // SSR should not call use effect
    // but just incase, do not run fingerprint js on ssr
    if (docId == null || isSSR) {
      return;
    }
    const asyncFn = async () => {
      const res = await FingerprintJS.load({
        token: env.NEXT_PUBLIC_FINGERPRINT_API_KEY,
        endpoint: ['https://metrics.docsha.re/nPposVJQU73LwGHr/ny80TRpqMA1TdLhN', FingerprintJS.defaultEndpoint],
        scriptUrlPattern: [
          'https://metrics.docsha.re/nPposVJQU73LwGHr/pdicFzkuFHy0PGxW?apiKey=<apiKey>&version=<version>&loaderVersion=<loaderVersion>',
          FingerprintJS.defaultScriptUrlPattern,
        ],
      });
      const result = await res.get();
      if (result.requestId != null && result.visitorId != null) {
        setResult({ requestId: result.requestId, visitorId: result.visitorId });
      }
    };
    asyncFn().catch(() => {
      // finger print failed to load
      // could be getting blocked, fall back to a random id on page load

      // make the request id be the current timestamp
      const randomSuffix = Math.random().toString(36).substring(2, 8);
      const requestId = Date.now().toFixed(0) + randomSuffix;
      // the server will use the UUID as the uniq view id
      setResult({ requestId: requestId, visitorId: undefined });
    });
  }, [docId, isSSR]);

  // Redirect to the raw file when the document can't be iframed. This lives in
  // its own effect keyed on the resolved sharing data — reading `data` inside
  // the mount-only fingerprint effect above captured a stale (usually
  // undefined) value, so the redirect frequently never fired once `data`
  // actually loaded.
  React.useEffect(() => {
    if (isSSR) return;
    if (navigateOnNotIFramed && data?.sharing.canBeIFramed === false && data?.sharing.fileUrl) {
      window.location.href = data.sharing.fileUrl;
    }
  }, [isSSR, navigateOnNotIFramed, data?.sharing.canBeIFramed, data?.sharing.fileUrl]);

  if (data === null) {
    return (
      <div className="flex h-screen items-center justify-center bg-black text-white">
        <div className="flex max-w-sm flex-col items-center gap-4 text-center">
          <Alerts.AlertCircle className="size-12 text-white/40" />
          <div>
            <div className="text-xl font-semibold">Document Not Found</div>
            <p className="mt-2 text-sm text-white/50">This document may have been removed or the link is no longer valid.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      {data?.sharing && (
        <SEO
          title={data.sharing.title}
          description={data.sharing.description ?? undefined}
          image={data.sharing.ogImageUrl ?? undefined}
          host={host}
        />
      )}
      {isLoading && (
        <div className="flex h-screen items-center justify-center bg-black">
          <WaveSpinner />
        </div>
      )}
      {!isLoading && data && (
        <div
          className={cn(
            'flex h-full flex-col items-stretch overflow-hidden bg-black text-xs sm:flex-row sm:text-base',
            readexPro.className,
          )}>
          {/* do not run pinger during ssr
              its dumb and wasteful to fingerprint our server */}
          {!isSSR && docId != null && !docId.startsWith('doc_') && result != null && (
            <Pinger docId={docId} requestId={result.requestId} visitorId={result.visitorId} />
          )}
          <DocumentContent data={data} />
          <DocumentAside data={data} />
        </div>
      )}
    </>
  );
};

type PingerProps = {
  requestId: string;
  visitorId: string | undefined;
  docId: string;
};
const Pinger = ({ docId, requestId, visitorId }: PingerProps) => {
  const { mutate } = api.sharingPublic.sync.useMutation();

  const recordView = React.useCallback(() => {
    mutate({ docId, visitorId, requestId, date: new Date() });
  }, [docId, visitorId, requestId, mutate]);

  React.useEffect(() => {
    const timer = setInterval(() => recordView(), 5000);
    return () => {
      clearInterval(timer);
    };
  }, [recordView]);

  return null;
};
